import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import React, { useState, useEffect, useRef, useMemo, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ShiftData from '../../../assets/data/ShiftData';
import TimezoneData from '../../../assets/data/TimezoneData';
import UserData from '../../../assets/data/UserData';
import EventAddByAdminIcon60pxSvg from '../../../assets/images/icons/EventAddByAdminIcon60pxSvg';
import avatar_2 from '../../../assets/images/logo/avatar-default-logo.png';
import profile from '../../../assets/images/logo/profile.png';
import { ControlpanelPrimaryButton, ControlpanelSecondaryButton, GrayIconButton } from '../../../components/StyledButton';
import { CustomDialog, StyledCheckbox } from '../../../components/StyledComponents';
import QuestionIcon16pxSvg from './../../../assets/images/icons/QuestionIcon16pxSvg';
import CustomInput from './../../../components/CustomInput';
import CustomSelect from './../../../components/CustomSelect';
import CustomDateTimePicker from '../../../components/CustomDateTimeRangePicker';
import DefaultTooltip from './../../../components/DefaultTooltip';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { editEventLog } from '../../../axios/Eventlog/EventLogApi';
import { useAuth } from '../../../context/AuthContext';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getShiftByUser } from '../../../axios/Shift/ShiftApi';

dayjs.extend(utc);
dayjs.extend(timezone);

const normalizeTime = (time) => {
   if (!time) return null;

   if (/^\d{2}:\d{2}$/.test(time)) {
      return `${time}:00`;
   }

   if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return time;
   }

   return null;
};

const buildDateTimeValue = (selectDate, selectTime) => {
   if (!selectDate) return null;

   if (!selectTime) return selectDate;

   return dayjs(
      `${dayjs(selectDate).format('YYYY-MM-DD')} ${normalizeTime(selectTime) || selectTime}`
   ).toDate();
};

const normalizeShiftOption = (shift) => ({
   ...shift,
   shiftId: shift?.shiftId ?? shift?.id,
   shiftName: shift?.shiftName ?? shift?.name ?? shift?.description,
});

const mergeShiftOptions = (...shiftLists) => {
   const shiftMap = new Map();

   shiftLists.flat().filter(Boolean).forEach((shift) => {
      const normalizedShift = normalizeShiftOption(shift);
      if (normalizedShift.shiftId == null) return;
      shiftMap.set(String(normalizedShift.shiftId), normalizedShift);
   });

   return Array.from(shiftMap.values());
};

const EventDateTimeField = memo(({
   selectDate,
   selectTime,
   dateTimeFormat,
   onDateTimeChange,
}) => (
   <CustomDateTimePicker
      mode="singleTime"
      value={buildDateTimeValue(selectDate, selectTime)}
      dateTimeFormat={dateTimeFormat}
      onChange={(newDateTime) => {
         const d = dayjs(newDateTime);
         if (!d.isValid()) return;

         onDateTimeChange(d.toDate(), d.format('HH:mm:ss'));
      }}
   />
), (prev, next) =>
   prev.selectDate === next.selectDate &&
   prev.selectTime === next.selectTime &&
   prev.dateTimeFormat === next.dateTimeFormat
);

const EditEventDialog = ({ onClose, usersData, shiftsData, onRefresh, info }) => {
   const { dateTimeFormat } = useAuth();
   const { t } = useTranslation();
   const normalizedInitialShifts = useMemo(
      () => mergeShiftOptions(shiftsData),
      [shiftsData]
   );
   const [shiftsData1, setShiftsData] = useState(normalizedInitialShifts);
   const [loadingCount, setLoadingCount] = useState(0);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const isDialogLoading = loadingCount > 0 || isSubmitting;
   const shiftFetchSeqRef = useRef(0);

   const browserTZ = dayjs.tz.guess();

   const parseDateTimeFromGMT = useCallback((timeGMT) => {
      if (!timeGMT) return { date: null, time: '' };

      const local = dayjs.utc(timeGMT).tz(browserTZ);

      return {
         date: local.toDate(),
         time: local.format('HH:mm:ss'),
      };
   }, [browserTZ]);

   const { date, time } = parseDateTimeFromGMT(info?.timeGMT);

   const user = usersData.find((u) => u.employeeCode === info?.employeeCode);
   const getCurrentShiftId = useCallback(
      () => {
         if (info?.shiftId != null) return info.shiftId;

         const matchedShift = normalizedInitialShifts.find(
            (shift) => info?.shiftName && String(shift.shiftName) === String(info.shiftName)
         );

         return matchedShift?.shiftId || '';
      },
      [info?.shiftId, info?.shiftName, normalizedInitialShifts]
   );

   const initialFormData = {
      eventType: info?.eventType === 'CHECK_IN' ? 1 : info?.eventType === 'CHECK_OUT' ? 2 : '',
      username: user ? `${user.fullName}` : `${info.fullName}`,
      userId: info?.userId,
      selectDate: date,
      selectTime: time,
      timeZone: info?.timeZone ? info.timeZone : '',
      shift: getCurrentShiftId(),
      shiftDate: info?.shiftDate
         ? new Date(info.shiftDate)
         : new Date(),
      latitude: info?.latitude || '',
      longitude: info?.longitude || '',
      description: info?.description || '',
      device: info?.deviceDescription || '',
   };

   const handleCloseDialog = () => {
      onClose();
   };

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prevData) => ({
         ...prevData,
         [name]: value,
      }));
   };

   const buildAttendanceDateTimeUTC = (date, time) => {
      if (!date || !time) return null;

      const datePart = format(date, 'yyyy-MM-dd');

      // 👉 tạo datetime theo timezone công ty
      const companyDateTime = dayjs.tz(
         `${datePart} ${time}`,
         'YYYY-MM-DD HH:mm:ss',
         browserTZ
      );

      // 👉 convert sang UTC gửi backend
      return companyDateTime.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
   };


   const handleSubmitEditEventLog = async () => {
      const attendanceDateTime = buildAttendanceDateTimeUTC(
         formData.selectDate,
         formData.selectTime
      );
      const payload = {
         ...formData,
         userId: formData.userName?.userId,
         useDifferentShiftDate,
         attendanceDateTime,
         shiftDate: format(formData.shiftDate, 'yyyy-MM-dd'),
         eventAttendanceId: info.eventAttendanceId,
         langId: localStorage.getItem("i18nextLngId")

      };

      setIsSubmitting(true);
      try {
         const response = await editEventLog(payload);
         if (response?.data?.responseCode === 0) {
            handleCloseDialog();
            toast.success(t('common.edit-event-log-successfully'));
            onRefresh();
         } else {
            toast.error(response?.data?.message);
         }
      } catch (error) {
         if (error.response) {
            if (error.response.status !== 401) {
               toast.error(t(error?.response.data));
            }
         } else {
            toast.error(t('common.network-timeout'));
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const [formData, setFormData] = useState(initialFormData);

   useEffect(() => {
      if (!normalizedInitialShifts.length) return;
      setShiftsData((prev) => mergeShiftOptions(prev, normalizedInitialShifts));
   }, [normalizedInitialShifts]);

   const eventDateTimeKey = useMemo(() => {
      if (!formData.selectDate || !formData.selectTime) return '';
      return `${dayjs(formData.selectDate).format('YYYY-MM-DD')}_${formData.selectTime}`;
   }, [formData.selectDate, formData.selectTime]);

   const handleDateTimeChange = useCallback((newDate, newTime) => {
      setFormData((prev) => ({
         ...prev,
         selectDate: newDate,
         selectTime: newTime,
      }));
   }, []);

   useEffect(() => {
      if (!formData.userId || !eventDateTimeKey) return;

      const selectDate = formData.selectDate;
      const selectTime = formData.selectTime;
      const nextSeq = shiftFetchSeqRef.current + 1;
      shiftFetchSeqRef.current = nextSeq;

      const fetchShift = async () => {
         const dateTimeUTC = buildAttendanceDateTimeUTC(selectDate, selectTime);
         if (!dateTimeUTC) return;

         setLoadingCount((prev) => prev + 1);
         try {
            const payload = {
               userId: formData.userId,
               dateTime: dateTimeUTC,
               langId: localStorage.getItem("i18nextLngId"),
            };

            const response = await getShiftByUser(payload);
            if (shiftFetchSeqRef.current !== nextSeq) return;

            const shiftList = (response.data.data || [])
               .map(normalizeShiftOption)
               .filter((shift) => shift.shiftId != null);

            setShiftsData(mergeShiftOptions(shiftList, normalizedInitialShifts));
         } catch (error) {
            if (shiftFetchSeqRef.current !== nextSeq) return;
            toast.error(t(error?.response?.data));
         } finally {
            // Luôn giảm count cho request đã tăng, kể cả khi bị thay bằng request mới
            setLoadingCount((prev) => Math.max(prev - 1, 0));
         }
      };

      fetchShift();
   }, [formData.userId, eventDateTimeKey]);

   const handleDataChange = (name, newData) => {
      setFormData((prevData) => ({
         ...prevData,
         [name]: newData,
      }));
   };


   const [useDifferentShiftDate, setUseDifferentShiftDate] = useState(false);

   const handleCheckedChange = (event) => {
      setUseDifferentShiftDate(event.target.checked);
   };

   // useEffect(() => {
   //    if(info?.shiftDate){
   //       setUseDifferentShiftDate(true)
   //    } else {
   //       setUseDifferentShiftDate(false)
   //    }

   // }, [info?.shiftDate]);


   // const userData = UserData();
   // const shiftsData = ShiftData();
   const timezoneData = TimezoneData();
   const [imageSrc, setImageSrc] = useState(avatar_2);

   useEffect(() => {
      if (!info?.eventAttendanceId) return;

      const { date: parsedDate, time: parsedTime } = parseDateTimeFromGMT(info?.timeGMT);

      // Invalidate in-flight shift fetch khi sync lại form từ info
      shiftFetchSeqRef.current += 1;

      setFormData({
         eventType: info?.eventType === 'CHECK_IN' ? 1 : info?.eventType === 'CHECK_OUT' ? 2 : '',
         username: user ? `${user.fullName}` : `${info?.fullName}`,
         userId: info?.userId,
         selectDate: parsedDate,
         selectTime: parsedTime,
         shift: getCurrentShiftId(),
         shiftDate: info?.shiftDate
            ? new Date(info.shiftDate)
            : new Date(),
         latitude: info?.latitude || '',
         longitude: info?.longitude || '',
         description: info?.description || '',
         device: info?.deviceDescription || '',
      });
   }, [info?.eventAttendanceId]);

   useEffect(() => {
      if (!shiftsData1.length) return;

      const sortedShifts = [...shiftsData1].sort((a, b) => b.shiftId - a.shiftId);

      setFormData((prev) => {
         if (prev.shift && sortedShifts.some((s) => String(s.shiftId) === String(prev.shift))) {
            return prev;
         }

         const matchedShift = sortedShifts.find((s) => {
            if (info?.shiftId != null && String(s.shiftId) === String(info.shiftId)) return true;
            if (info?.shiftName && String(s.shiftName) === String(info.shiftName)) return true;
            return false;
         });

         return {
            ...prev,
            shift: matchedShift?.shiftId || sortedShifts[0]?.shiftId || '',
         };
      });
   }, [shiftsData1, info?.shiftName]);


   return (
      <CustomDialog open transitionDuration={300} maxWidth={'xl'} loading={isDialogLoading} onClose={handleCloseDialog} keepMounted>
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold '>{t('common.edit-event')}</p>
               </div>
               <GrayIconButton onClick={handleCloseDialog} disabled={isDialogLoading}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>
         <DialogContent>
            <div className='pt-5'>
               {info.image && (
                  <div className='flex justify-between items-start pb-5'>
                     <div className='p-1 rounded border border-solid border-[#E5E7EB] max-w-40'>
                        <img src={info.image ? `data:image/png;base64,${info.image}` : imageSrc} alt="avatar" className="" />
                     </div>
                     <div>
                        <img src={profile} className='' />
                     </div>

                     {/* <EventAddByAdminIcon60pxSvg /> */}
                  </div>
               )}
               <p>{t('common.event-type')}<span className='text-[#EF4444] ml-1'>*</span></p>
               <FormControl
                  component='fieldset'
                  sx={{
                     '.MuiFormGroup-root.MuiFormGroup-row': {
                        gap: '20px',
                     },
                  }}
               >
                  <RadioGroup row aria-label='Event type' name='eventType' value={formData.eventType} onChange={handleInputChange}>
                     <FormControlLabel value={1} control={<Radio size='small' />} label={t('attendance.CHECK_IN')} />
                     <FormControlLabel value={2} control={<Radio size='small' />} label={t('attendance.CHECK_OUT')} />
                     {/* <FormControlLabel value={3} control={<Radio size='small' />} label='Pending check-in' /> 
                     <FormControlLabel value={4} control={<Radio size='small' />} label=' Pending check-out' /> */}
                     {/* <FormControlLabel value='access' control={<Radio size='small' />} label='Access' /> */}
                  </RadioGroup>
               </FormControl>
            </div>
            <div className='grid grid-cols-12 md:gap-x-3 gap-y-6'>
               <div className='col-span-12 md:col-span-6 mt-4'>
                  <label>
                     {t('common.userName')}

                  </label>
                  <CustomInput
                     name='username'
                     inputType='text'
                     readOnly={true}
                     value={formData.username}
                     handleInputChange={handleInputChange}
                     labelEnable={false}
                     className='mt-2'
                     placeholder={t('common.userName')}
                  />
               </div>
               <div className='col-span-12 md:col-span-6 mt-4'>
                  <label>{t('common.date-time')}</label><span className='text-[#EF4444] ml-1'>*</span>
                  <div className='mt-2'>
                     <EventDateTimeField
                        selectDate={formData.selectDate}
                        selectTime={formData.selectTime}
                        dateTimeFormat={dateTimeFormat}
                        onDateTimeChange={handleDateTimeChange}
                     />
                  </div>
               </div>
               {/* <div className='col-span-12 md:col-span-3'>
                  <label>{t('common.time-zone')}</label><span className='text-[#EF4444] ml-1'>*</span>
                  <CustomSelect
                     name='timeZone'
                     value={formData.timeZone}
                     onChange={handleInputChange}
                     options={timezoneData}
                     className='mt-2'
                  />
               </div> */}
               <div className='col-span-12 md:col-span-6'>
                  <label>{t('common.shift')}</label><span className='text-[#EF4444] ml-1'>*</span>
                  <CustomSelect
                     name='shift'
                     value={formData.shift}
                     onChange={handleInputChange}
                     options={shiftsData1}
                     className='mt-2'
                     dataType='shift'
                  />
               </div>
               {/* <div className='col-span-12 md:col-span-6'>
                  <div className='flex gap-1 items-center'>
                     <label>Shift date</label>
                     <DefaultTooltip
                        title='Changing shift date'
                        readMore={false}
                        content={
                           <div className='flex flex-col gap-3'>
                              <p className='font-semibold'>By default, "Shift date" is same as "Event Date".</p>
                              <p>
                                 When an employee Check-in or Check-out not in the same date as the event date, (E.g Working overtime or
                                 Shift spans for two days) shift date should be specified. Otherwise the event will belong to current day's
                                 shift.
                              </p>
                           </div>
                        }
                     >
                        <div>
                           <QuestionIcon16pxSvg className='fill-[#BDBDBD]' />
                        </div>
                     </DefaultTooltip>
                  </div>
                  <div>
                     <FormControlLabel
                        className='mt-1 !-ml-1'
                        control={
                           <StyledCheckbox
                              checked={useDifferentShiftDate}
                              onChange={handleCheckedChange}
                              name='useDifferentShiftDate'
                              disableRipple
                           />
                        }
                        label='Use a different shift date'
                     />
                  </div>
                  <CustomDatePicker
                     name='selectDate'
                     value={formData.selectDate}
                     onChange={(newDate) => handleDataChange('selectDate', newDate)}
                     readOnly={true}
                     className='!mt-2'
                     disabledOpenPicker={!useDifferentShiftDate}
                  />
               </div> */}

               {/* <div className='col-span-12 md:col-span-6 self-end'>
                  <label>Location (Latitude, Longitude)</label>
                  <div className='flex items-center mt-2'>
                     <CustomInput
                        name='latitude'
                        inputType='text'
                        value={formData.latitude}
                        handleInputChange={handleInputChange}
                        labelEnable={false}
                        placeholder='Latitude'
                        className='rounded-tr-none rounded-br-none'
                     />
                     <CustomInput
                        name='longitude'
                        inputType='text'
                        value={formData.longitude}
                        handleInputChange={handleInputChange}
                        labelEnable={false}
                        placeholder='Longitude'
                        className='rounded-tl-none rounded-bl-none'
                     />
                  </div>
               </div> */}
               <div className='col-span-12 md:col-span-6'>
                  <label>{t('common.description')}</label>
                  <CustomInput
                     name='description'
                     inputType='text'
                     value={formData.description}
                     handleInputChange={handleInputChange}
                     labelEnable={false}
                     className='mt-2'
                     placeholder={t('common.description')}
                  />
               </div>
               {/* <div className='col-span-12 md:col-span-6'>
                  <label>{t('common.device')}</label>
                  <CustomInput
                     name='device'
                     inputType='text'
                     value={formData.device}
                     handleInputChange={handleInputChange}
                     labelEnable={false}
                     className='mt-2'
                     placeholder={t('common.device')}
                  />
               </div> */}
               {/* <div className='col-span-12 md:col-span-6'>
                  <label>Authentication</label> 
                  <p className='mt-2'>[No identification data]</p>
               </div> */}
            </div>
         </DialogContent>
         <DialogActions>
            <div className='flex gap-2 px-4 py-4'>
               <ControlpanelSecondaryButton onClick={handleCloseDialog} disabled={isDialogLoading}>
                  <p>{t('button.close')}</p>
               </ControlpanelSecondaryButton>
               <ControlpanelPrimaryButton onClick={(e) => {
                  handleSubmitEditEventLog()
               }} disabled={isDialogLoading}>
                  <p>{t('button.update')}</p>
               </ControlpanelPrimaryButton>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default EditEventDialog;
